/**
 * Pure argv + config construction for `opencode run`. Kept separate from the
 * harness so both are unit-testable without spawning anything.
 */

import { findOnPath, TuiUnavailableError } from "../pty/adapters/types.js";
import type { McpConfigEntry } from "../pty/tool-server.js";

// Re-exported so the harness has a single import surface for spawn concerns.
export {
  buildOpenCodeChildEnv,
  prepareOpenCodeCredentials,
  resolveOpenCodeModel,
} from "./credentials.js";

/**
 * Resolve the opencode binary, honoring `CONVEYOR_OPENCODE_BIN`.
 * Throws `TuiUnavailableError` when it cannot run — the runner turns that into a
 * card-visible failure rather than a crash.
 */
export function resolveOpenCodeBinary(env: NodeJS.ProcessEnv = process.env): string {
  const override = env.CONVEYOR_OPENCODE_BIN;
  const found = override ? findOnPath(override, env) : findOnPath("opencode", env);
  if (!found) {
    throw new TuiUnavailableError(
      "opencode",
      "The opencode CLI is not available in this environment. It must be baked " +
        "into the pod image (see Dockerfile.base) — re-run Build Image for this " +
        "project, or set CONVEYOR_OPENCODE_BIN to its absolute path.",
    );
  }
  return found;
}

/** Defensive bound on the prompt passed as argv (Linux MAX_ARG_STRLEN is 128KiB). */
export const PROMPT_MAX_CHARS = 96_000;

export interface RunArgsInput {
  prompt: string;
  /** The workspace to work in. Passed as `--dir`; see buildRunArgs. */
  cwd: string;
  model?: string;
  /** opencode session id to continue, or null for a fresh session. */
  resumeSessionId?: string | null;
}

/** Tools that can mutate the workspace — denied outright in read-only modes. */
export const MUTATING_TOOLS = ["edit", "bash", "external_directory"] as const;

/**
 * Build the argv for `opencode run`.
 *
 * `--format json` is what makes this a structured harness at all. `--auto`
 * auto-approves permissions, matching the `bypassPermissions` posture the card
 * harness already runs under — without it a headless run would block on a
 * permission prompt that nothing can answer.
 *
 * The prompt is a positional argument, so it is truncated rather than risking
 * E2BIG. Truncation is marked so a reader can tell it happened.
 */
export function buildRunArgs(input: RunArgsInput): string[] {
  // `--dir` is REQUIRED, not belt-and-braces: opencode resolves its working root
  // independently of the spawned process's cwd. Passing cwd to child_process
  // alone is not enough — observed live, a run whose child cwd was the scratch
  // checkout wrote its file into the PARENT process's directory instead. An
  // opencode card would silently do its work in the wrong repo.
  // `--auto` auto-approves permissions that are not EXPLICITLY denied, so it
  // composes with the read-only denies in buildOpenCodeConfigContent: a
  // read-only run still never blocks on an approval nothing can answer.
  const args = ["run", "--dir", input.cwd, "--format", "json", "--auto"];
  if (input.model) {
    // opencode expects provider/model; a bare model name is passed through and
    // resolved against the configured default provider.
    args.push("--model", input.model);
  }
  if (input.resumeSessionId) args.push("--session", input.resumeSessionId);
  args.push(truncatePrompt(input.prompt));
  return args;
}

export function truncatePrompt(prompt: string): string {
  if (prompt.length <= PROMPT_MAX_CHARS) return prompt;
  return `${prompt.slice(0, PROMPT_MAX_CHARS)}\n\n[prompt truncated at ${PROMPT_MAX_CHARS} characters]`;
}

export interface OpenCodeConfigInput {
  /** MCP entries as Conveyor's tool server reports them. */
  entries: Record<string, McpConfigEntry>;
  /**
   * Absolute path to a file holding the Conveyor system prompt (task context,
   * plan, mode instructions), or null when there is none.
   */
  systemPromptPath?: string | null;
  /**
   * Read-only mode (Claude's `permissionMode: "plan"` equivalent). Denies the
   * mutating tools instead of relying on the model to behave.
   */
  readOnly?: boolean;
  /**
   * Absolute path to the staged Conveyor events plugin (see ./plugin.ts), or
   * null/absent when the caller has no event sink (the headless run path —
   * its NDJSON stdout already IS the event source).
   */
  pluginPath?: string | null;
}

/**
 * Build the inline `OPENCODE_CONFIG_CONTENT` for one run.
 *
 * Inline rather than a file because both file locations have side effects:
 * `opencode.json` in the workspace root dirties the checked-out repo, and the
 * global `~/.config/opencode` leaks between concurrent sessions.
 *
 * Three things ride it:
 *   - `mcp`: Conveyor already serves its tools over loopback StreamableHTTP for
 *     the Claude path, and opencode's `type: "remote"` connects to exactly that
 *     (verified: reports `connected`, and a run invoked a tool through it).
 *     External stdio entries (the baked `playwright-mcp`) map to `type: "local"`.
 *   - `instructions`: how the system prompt reaches opencode. There is no
 *     `--append-system-prompt`; a config `instructions` file is the supported
 *     route (verified: a rule in that file changed the model's replies).
 *   - `permission`: ALWAYS emitted. `"*": "allow"` is what keeps an agent turn
 *     from parking on an approval nothing can answer — the headless run pairs
 *     it with `--auto`, and the interactive TUI (which has no `--auto`) relies
 *     on it alone. Read-only layers explicit denies on top; because the TUI's
 *     turns run under the same config, the denies bind human-typed turns too.
 *   - `plugin`: the staged Conveyor events plugin (PTY path only), loaded via
 *     the probe-verified `file://` specifier.
 *
 * Returns null when there is nothing to configure — which no longer happens
 * for real callers (the permission block is unconditional), but the guard is
 * kept so a future all-optional call cannot inject an empty override.
 */
export function buildOpenCodeConfigContent(input: OpenCodeConfigInput): string | null {
  const { entries, systemPromptPath, readOnly, pluginPath } = input;
  const mcp: Record<string, unknown> = {};
  for (const [name, entry] of Object.entries(entries)) {
    if (entry.type === "http") {
      mcp[name] = {
        type: "remote",
        url: entry.url,
        enabled: true,
        ...(Object.keys(entry.headers).length > 0 ? { headers: entry.headers } : {}),
      };
    } else {
      mcp[name] = {
        type: "local",
        command: [entry.command, ...(entry.args ?? [])],
        enabled: true,
        ...(entry.env ? { environment: entry.env } : {}),
      };
    }
  }
  const config: Record<string, unknown> = {};
  if (Object.keys(mcp).length > 0) config.mcp = mcp;
  if (systemPromptPath) config.instructions = [systemPromptPath];
  if (pluginPath) config.plugin = [`file://${pluginPath}`];
  config.permission = {
    "*": "allow",
    ...(readOnly ? Object.fromEntries(MUTATING_TOOLS.map((tool) => [tool, "deny"])) : {}),
  };
  if (Object.keys(config).length === 0) return null;
  return JSON.stringify(config);
}
